import { useState } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { Wind, MapPin, Settings, X, RefreshCw, Navigation } from 'lucide-react'
import { windTideAPI, widgetAPI } from '../../services/api'
import { getPreferredZipCode, savePreferredZipCode } from '../../utils/zipCodePreference'
import toast from 'react-hot-toast'

interface WindWidgetProps {
  widget: any
  isExpanded: boolean
}

export default function WindWidget({ widget, isExpanded }: WindWidgetProps) {
  const [zipCode, setZipCode] = useState(widget.config?.zipCode || getPreferredZipCode())
  const [tempZipCode, setTempZipCode] = useState(zipCode)
  const [showSettings, setShowSettings] = useState(false)
  const queryClient = useQueryClient()

  // Fetch wind data
  const { data: windData, isLoading, refetch } = useQuery({
    queryKey: ['wind-data', zipCode],
    queryFn: async () => {
      const { data } = await windTideAPI.getData(zipCode)
      return data
    },
    enabled: !!zipCode,
  })

  // Update widget config mutation
  const updateWidgetMutation = useMutation({
    mutationFn: async (newZipCode: string) => {
      const newConfig = {
        ...widget.config,
        zipCode: newZipCode,
      }
      const { data } = await widgetAPI.update(widget.id, {
        config_data: JSON.stringify(newConfig),
      })
      return data
    },
    onSuccess: () => {
      toast.success('ZIP code saved!')
      queryClient.invalidateQueries({ queryKey: ['widgets'] })
    },
    onError: () => {
      toast.error('Failed to save ZIP code')
    },
  })

  const handleUpdateZipCode = async () => {
    setZipCode(tempZipCode)
    setShowSettings(false)
    
    // Save to shared preference for other widgets
    savePreferredZipCode(tempZipCode)
    
    await updateWidgetMutation.mutateAsync(tempZipCode)
  }

  const getWindSpeedCategory = (speed: number) => {
    if (speed < 5) return { label: 'Calm', color: 'text-green-600', bg: 'bg-green-50' }
    if (speed < 12) return { label: 'Light', color: 'text-blue-600', bg: 'bg-blue-50' }
    if (speed < 19) return { label: 'Moderate', color: 'text-yellow-600', bg: 'bg-yellow-50' }
    if (speed < 25) return { label: 'Fresh', color: 'text-orange-600', bg: 'bg-orange-50' }
    return { label: 'Strong', color: 'text-red-600', bg: 'bg-red-50' }
  }

  const getWindDirectionRotation = (direction: string) => {
    const rotations: Record<string, number> = {
      N: 0, NNE: 22.5, NE: 45, ENE: 67.5,
      E: 90, ESE: 112.5, SE: 135, SSE: 157.5,
      S: 180, SSW: 202.5, SW: 225, WSW: 247.5,
      W: 270, WNW: 292.5, NW: 315, NNW: 337.5
    }
    return rotations[direction] || 0
  }

  if (showSettings) {
    return (
      <div className="h-full flex flex-col p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Settings</h3>
          <button onClick={() => setShowSettings(false)} className="p-1 rounded hover:opacity-70" style={{ color: 'var(--color-text-primary)' }}>
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
              ZIP Code
            </label>
            <input
              type="text"
              value={tempZipCode}
              onChange={(e) => setTempZipCode(e.target.value)}
              placeholder="Enter ZIP code"
              className="w-full px-3 py-2 border rounded-lg"
              style={{ 
                backgroundColor: 'var(--color-surface)', 
                borderColor: 'var(--color-border)', 
                color: 'var(--color-text-primary)' 
              }}
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>Popular ZIP Codes:</strong>
            </p>
            <ul className="text-xs text-blue-700 mt-2 space-y-1">
              <li>• 33139 - Miami Beach, FL</li>
              <li>• 02134 - Boston, MA</li>
              <li>• 94102 - San Francisco, CA</li>
              <li>• 92037 - La Jolla, CA</li>
              <li>• 10001 - New York, NY</li>
            </ul>
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button
            onClick={handleUpdateZipCode}
            className="flex-1 px-4 py-2 text-white rounded-lg transition"
            style={{ backgroundColor: 'var(--color-button-bg, #0183c6)' }}
          >
            Update
          </button>
          <button
            onClick={() => setShowSettings(false)}
            className="px-4 py-2 rounded-lg transition hover:opacity-80"
            style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text-primary)' }}
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <RefreshCw className="animate-spin text-gray-400" size={32} />
      </div>
    )
  }

  if (!windData) {
    return (
      <div className="h-full flex items-center justify-center p-4 text-center">
        <div>
          <Wind size={48} className="mx-auto mb-2 text-gray-300" />
          <p className="text-gray-600 mb-3">No data available</p>
          <button
            onClick={() => setShowSettings(true)}
            className="px-4 py-2 text-white rounded-lg transition"
            style={{ backgroundColor: 'var(--color-button-bg, #0183c6)' }}
          >
            Set ZIP Code
          </button>
        </div>
      </div>
    )
  }

  const windCategory = getWindSpeedCategory(windData.wind_speed)

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-widget-bg)' }}>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <MapPin size={14} className="flex-shrink-0" style={{ color: 'var(--color-primary)' }} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate" style={{ color: 'var(--color-text-primary)' }}>
              {windData.location_name}
            </div>
            <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{zipCode}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => refetch()}
            className="p-1.5 rounded transition hover:opacity-70"
            style={{ color: 'var(--color-text-primary)' }}
            title="Refresh"
          >
            <RefreshCw size={14} />
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="p-1.5 rounded transition hover:opacity-70"
            style={{ color: 'var(--color-text-primary)' }}
            title="Settings"
          >
            <Settings size={14} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {/* Compact View - Widget */}
        {!isExpanded ? (
          <div className="space-y-3">
            {/* Main Wind Display */}
            <div className="flex items-center justify-between gap-4">
              {/* Wind Speed */}
              <div className="flex-1 text-center">
                <div className="text-4xl font-bold" style={{ color: 'var(--color-primary)' }}>
                  {windData.wind_speed}
                </div>
                <div className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>mph</div>
                <div className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold mt-1 ${windCategory.bg} ${windCategory.color}`}>
                  {windCategory.label}
                </div>
              </div>

              {/* Wind Direction */}
              <div className="flex-1 text-center">
                <div className="relative inline-block">
                  <Navigation
                    size={40}
                    style={{
                      color: 'var(--color-primary)',
                      transform: `rotate(${getWindDirectionRotation(windData.wind_direction)}deg)`,
                      transition: 'transform 0.3s ease',
                    }}
                  />
                </div>
                <div className="text-sm font-semibold mt-1" style={{ color: 'var(--color-text-primary)' }}>{windData.wind_direction}</div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="grid grid-cols-3 gap-2 text-xs">
              {windData.wind_gust && (
                <div className="text-center p-2 rounded" style={{ backgroundColor: 'var(--color-surface)' }}>
                  <div style={{ color: 'var(--color-text-secondary)' }}>Gusts</div>
                  <div className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{windData.wind_gust}</div>
                </div>
              )}
              <div className="text-center p-2 rounded" style={{ backgroundColor: 'var(--color-surface)' }}>
                <div style={{ color: 'var(--color-text-secondary)' }}>Temp</div>
                <div className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{windData.temperature}°F</div>
              </div>
              <div className="text-center p-2 rounded" style={{ backgroundColor: 'var(--color-surface)' }}>
                <div style={{ color: 'var(--color-text-secondary)' }}>Sky</div>
                <div className="font-semibold truncate" style={{ color: 'var(--color-text-primary)' }}>{windData.conditions}</div>
              </div>
            </div>
          </div>
        ) : (
          /* Expanded View - Full Details */
          <div className="rounded-lg border-2 p-4" style={{ backgroundColor: 'var(--color-widget-bg)', borderColor: 'var(--color-border)' }}>
            <div className="flex items-center gap-2 mb-4">
              <Wind size={20} style={{ color: 'var(--color-primary)' }} />
              <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Wind Conditions</h3>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              {/* Wind Speed */}
              <div className="text-center">
                <div className="text-5xl font-bold" style={{ color: 'var(--color-primary)' }}>
                  {windData.wind_speed}
                </div>
                <div className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>mph</div>
                <div className={`inline-block px-3 py-1 rounded-full text-sm font-semibold mt-2 ${windCategory.bg} ${windCategory.color}`}>
                  {windCategory.label}
                </div>
              </div>

              {/* Wind Direction */}
              <div className="text-center">
                <div className="relative inline-block mb-2">
                  <Navigation
                    size={56}
                    style={{
                      color: 'var(--color-primary)',
                      transform: `rotate(${getWindDirectionRotation(windData.wind_direction)}deg)`,
                      transition: 'transform 0.3s ease',
                    }}
                  />
                </div>
                <div className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>{windData.wind_direction}</div>
                <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Direction</div>
              </div>
            </div>

            {windData.wind_gust && (
              <div className="pt-3 border-t text-center" style={{ borderColor: 'var(--color-border)' }}>
                <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  Gusts up to <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{windData.wind_gust} mph</span>
                </span>
              </div>
            )}

            <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-3 text-sm" style={{ borderColor: 'var(--color-border)' }}>
              <div className="text-center p-2 rounded" style={{ backgroundColor: 'var(--color-surface)' }}>
                <div style={{ color: 'var(--color-text-secondary)' }}>Temperature</div>
                <div className="text-xl font-bold mt-1" style={{ color: 'var(--color-text-primary)' }}>{windData.temperature}°F</div>
              </div>
              <div className="text-center p-2 rounded" style={{ backgroundColor: 'var(--color-surface)' }}>
                <div style={{ color: 'var(--color-text-secondary)' }}>Conditions</div>
                <div className="text-xl font-bold mt-1" style={{ color: 'var(--color-text-primary)' }}>{windData.conditions}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

